<template lang="pug">
BaseModal.your-plan-modal(@backdropClick="$emit('close')")
	template(v-slot:header)
		.course-list-header(v-if="page === 'courseList'" :class="{ movable }")
			p.text-year(@click="page = movable ? 'yearList' : 'courseList'") {{ sectionInfo.year }} year
			.nav(:class="{ required: sectionRequired, clickable: !sectionRequired }" @click="viewGradCredits")
				p.nav-label {{ isSubjectSelected ? section.label : 'No Subject Selected' }}
				IconChevron.icon-chevron(v-if="!sectionRequired" :direction="'down'")
		.year-list-header(v-else-if="page === 'yearList'")
			.nav.clickable(@click="page = 'courseList'")
				p.nav-label Select Year
		.grad-credits-header(v-else-if="page === 'gradCreditList'")
			.nav.clickable(@click="page = 'courseList'")
				p.nav-label Select Subject
				//- IconChevron.icon-chevron(direction="up")
		.child-grad-credits-header(v-else)
			.nav.clickable(@click="page = 'gradCreditList'")
				IconChevron.icon-chevron(direction="left")
				p.nav-label {{ parentGradCreditName }}
		IconClose.icon-close(@click="$emit('close')")
	template(v-slot:body)
		.course-list-body(v-if="page === 'courseList'")
			.no-courses(v-if="courses.length === 0")
				p.no-courses-text No courses selected
			.courses(v-else)
				CourseCard(
					v-for="(course, i) in courses"
					class="course-card"
					:key="i"
					:course="course"
					:planned="isCoursePlanned(course.id)"
					@unplan="unplanCourse(course.id)"
				)
		.year-list-body(v-else-if="page === 'yearList'")
			.list-item(
				v-for="year in years"
				:key="year"
				@click="selectYear(year)"
			)
				p.label.capitalize {{ year }}
				IconChevron.icon-chevron
		.grad-credits-body(v-else="page === 'gradCreditList'")
			.list-item(
				v-for="(gc, i) in displayedGradCredits"
				:key="gc.id"
				@click="selectGradCredit(gc)"
			)
				p.label {{ `${gc.name} ${page === 'childGradCreditList' && i === 0 ? '(All Courses)' : ''}` }}
				IconChevron.icon-chevron
	template(v-slot:footer)
		.add-course-button(
			v-if="page === 'courseList' && isSubjectSelected"
			class="button"
			@click="onAddCourseClick"
		) + Add Course
</template>

<script>
import BaseModal from '@/components/BaseModal';
import IconClose from '@/components/IconClose';
import IconChevron from '@/components/IconChevron';
import CourseCard from '@/components/CourseCard';

export default {
  name       : 'your-plan-modal',
  components : {
    BaseModal,
    IconClose,
    IconChevron,
    CourseCard
  },

  props : {
    // Object containing the year and index of the section selected
    sectionInfo : Object
  },

  data() {
    return {
      selectingSubject : false,

      // Determines which "page" of the modal to display. E.g., a grid of
      // all planned course cards, or a list of grad credits to change to.
      // Either 'courseList' | 'yearList' | 'gradCreditList' | 'childGradCreditList'
      page : 'courseList',

      // If on 'parentGradCredit' page and the selected grad credit has children,
      // this tracks which grad credit was selected.
      parentGradCreditID : '',
    };
  },

  computed : {
    studentPlan() {
      return this.$store.getters.studentPlan;
    },

    section() {
      const { year, index } = this.sectionInfo;
      return this.studentPlan[year][index];
    },

    sectionRequired() {
      return this.section.gradRequired;
    },

    parentGradCreditName() {
      return this.$store.getters.getGradCreditByID( this.parentGradCreditID ).name;
    },

    // Checks if the subject is selected by checking the gradCredits array
    isSubjectSelected() {
      return this.section.gradCredits && this.section.gradCredits.length > 0;
    },

    movable() {
      return this.section.movable;
    },

    courses() {
      const allCourses = this.$store.getters.courses || [];
      const courses = this.section.courses
        ? this.section.courses
          .map( id => allCourses.find( course => course.id === id ) )
        // Shouldn't be any undefined after, but just in case allCourses didn't find it for some reason
          .filter( course => course !== undefined )
        : [];
      return courses;
    },

    years() {
      return ['freshman', 'sophomore', 'junior', 'senior'];
    },

    displayedGradCredits() {
      const allCredits = this.$store.getters.gradCredits;

      // Return only credits that are not child credits
      if ( this.page === 'gradCreditList' ) {
        return allCredits.filter( credit => !credit.parentGradCredit );
      }

      // Otherwise, return the children grad credits with the parent first
      const parent = allCredits.find( gc => gc.id === this.parentGradCreditID );
      return [parent].concat(
        allCredits.filter( credit => credit.parentGradCredit === this.parentGradCreditID )
      );
    }
  },

  mounted() {
    // console.log( 'your plan modal:', this.sectionInfo );
  },

  methods : {
    onAddCourseClick() {
      this.$emit( 'addCourse' );
    },

    isCoursePlanned( courseID ) {
      return this.$store.getters.isCoursePlanned( courseID );
    },

    unplanCourse( courseID ) {
      const { year, index } = this.sectionInfo;
      this.$store.dispatch( 'unplanCourse', {
        year, index, courseID
      } );
    },

    viewGradCredits() {
      if ( this.sectionRequired ) return;
      this.page = 'gradCreditList';
    },

    selectGradCredit( gradCredit ) {
      // Check if we should navigation to see child grad credits
      const creditHasChildren = this.$store.getters.gradCredits.filter( gc => gc.parentGradCredit === gradCredit.id ).length > 0;
      if ( creditHasChildren && this.page !== 'childGradCreditList' ) {
        // console.log( 'selected credit has children', gradCredit );
        this.parentGradCreditID = gradCredit.id;
        this.page = 'childGradCreditList';
        return;
      }
      // Otherwise, selected a credit and navigate back to courseList
      this.page = 'courseList';
      this.$emit( 'changeGradCredit', gradCredit );
    },

    selectYear( year ) {
      this.page = 'courseList';
      this.$emit( 'changeYear', year );
    }
  }
};
</script>

<style lang="scss" scoped>
.your-plan-modal {
	// padding: 30px;

	@include sm {
		padding: 30px;
	}
}

$light-blue: #5D9BFF;

.your-plan-modal::v-deep .modal {
	width: 90%;
	max-height: 80vh;
	display: flex;
	flex-direction: column;

	@include sm {
		width: auto;
		min-width: 400px;
		min-height: 300px;
	}

	.modal-header {
		position: relative;
		background: white;
		color: #36548D;
		font-weight: 600;
		padding: 20px;

		.icon-chevron {
			width: 10px;
			margin-left: 10px;
		}
		.nav {
			font-size: 20px;
			display: flex;
			align-items: center;
			.nav-label {
				font-size: 20px;
			}
			transition: all 0.2s ease;
			&.clickable:hover {
				cursor: pointer;
				color: $light-blue;
				.nav-label {
					border-bottom: 2px solid $light-blue;
					margin-bottom: -2px;
				}
			}
		}

		.course-list-header {
			&.movable .text-year:hover {
				cursor: pointer;
				color: $light-blue;
				border-bottom: 2px solid $light-blue;
				margin-bottom: -2px;
			}
			.text-year {
				text-transform: uppercase;
				display: inline-block;
			}
			.text-nav {
				font-size: 20px;
				cursor: pointer;
				&.required {
					&::before {
						content: '*';
						color: $close;
					}
				}
			}
			.icon-chevron {
				width: 10px;
			}
		}

		.child-grad-credits-header .nav .icon-chevron {
			margin-right: 10px;
		}

		.icon-close {
			position: absolute;
			top: 10px;
			right: 10px;
			color: $close;
		}
	}

	.modal-body {
		/* display: flex; */
		flex-direction: column;
		// justify-content: center;
		// align-items: center;
		flex: 1;
		overflow: auto;

		.course-list-body {
			width: 100%;
			height: 100%;
			padding: 20px;
			padding-top: 10px;

			.courses {
				display: flex;
				flex-wrap: wrap;
				/* grid-template-columns: repeat(2, 1fr); */
				overflow: auto;
				padding: 10px;
				justify-content: center;
				gap: 20px;
				/* min-width: 600px; */

				.course-card {
					max-width: 400px;
				}

				@include md {
					grid-template-columns: repeat(2, 1fr);
				}
				@include lg {
					margin: 0 40px;
				}
			}
			.no-courses {
				display: flex;
				justify-content: center;
				align-items: center;

				.no-courses-text {
					font-style: italic;
					margin-top: 30px;
				}
			}
		}

		.grad-credits-body {
			width: 100%;
			margin-bottom: 10px;
		}

		.list-item {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 8px 20px;
			padding-right: 30px;
			cursor: pointer;
			transition: background-color .2s ease;
			&:hover {
				background-color: rgba(38, 104, 208, 0.12);
			}
			.capitalize {
				text-transform: capitalize
			}
			.icon-chevron {
				width: 8px;
			}
		}
	}

	.modal-footer {
		// flex: 1;
		// display: flex;
		// flex-direction: column;
		margin-top: auto;

		.add-course-button {
			margin: 20px;
			margin-top: auto;
		}
	}
}
</style>
